import React from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader,
  Typography, 
  Box, 
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Skeleton
} from '@mui/material';
import { Circle as CircleIcon } from '@mui/icons-material';

const StatsCard = ({ 
  title, 
  data = [], 
  loading = false,
  maxItems = 5,
  showPercentages = false,
  colorPalette = ['primary', 'secondary', 'success', 'warning', 'error']
}) => {

  const formatValue = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString('he-IL');
    }
    return value;
  };

  const calculatePercentage = (value, total) => {
    if (!total || total === 0) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  const getTotalValue = () => {
    return data.reduce((sum, item) => sum + (item.value || 0), 0);
  };

  const renderSkeletons = () => (
    Array.from({ length: maxItems }).map((_, index) => (
      <ListItem key={index} sx={{ py: 1 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <Skeleton variant="circular" width={16} height={16} />
        </ListItemIcon>
        <ListItemText
          primary={<Skeleton variant="text" width="60%" />}
          secondary={<Skeleton variant="text" width="30%" />}
        />
      </ListItem>
    ))
  );

  const renderDataItems = () => {
    const total = getTotalValue();
    const displayData = data.slice(0, maxItems);

    return displayData.map((item, index) => {
      const colorIndex = index % colorPalette.length;
      const color = colorPalette[colorIndex];
      const percentage = showPercentages ? calculatePercentage(item.value, total) : null;

      return (
        <React.Fragment key={item.label || index}>
          <ListItem sx={{ 
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CircleIcon 
                sx={{ 
                  fontSize: 16,
                  color: `${color}.main`
                }} 
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: `${color}.main`
                    }}
                  >
                    {formatValue(item.value)}
                  </Typography>
                </Box>
              }
              secondary={
                showPercentages && percentage && (
                  <Chip
                    size="small"
                    label={`${percentage}%`}
                    color={color}
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.7rem',
                      height: 20
                    }}
                  />
                )
              }
            />
          </ListItem>
          {index < displayData.length - 1 && (
            <Divider variant="inset" component="li" />
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <Card sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {title}
          </Typography>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ 
        flex: 1,
        pt: 0,
        '&:last-child': { pb: 2 }
      }}>
        {loading ? (
          <List dense>
            {renderSkeletons()}
          </List>
        ) : data.length > 0 ? (
          <List dense sx={{ py: 0 }}>
            {renderDataItems()}
          </List>
        ) : (
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 120,
            color: 'text.secondary'
          }}>
            <Typography variant="body2">
              אין נתונים להצגה
            </Typography>
          </Box>
        )}

        {/* סיכום כללי */}
        {showPercentages && data.length > 0 && !loading && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1
            }}>
              <Typography variant="body2" color="text.secondary">
                סה"כ:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {formatValue(getTotalValue())}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StatsCard;
